import { readonly, shallowRef, toValue, type MaybeRefOrGetter } from "vue";
import {
  assertAutomationRunCompleted,
  resolvePdfDownloadFileName,
  waitForAutomationRun,
  type AutomationRunSummary,
} from "#bb/lib/pdfReportDownload";
import { resolveFileApiPath } from "#bb/lib/pathContract";

interface UsePdfReportDownloadOptions {
  automationPath: MaybeRefOrGetter<string | null | undefined>;
  reportPath: MaybeRefOrGetter<string | null | undefined>;
  fileName: MaybeRefOrGetter<string | null | undefined>;
  resourceDefinitionPath?: MaybeRefOrGetter<string | null | undefined>;
}

interface AutomationRunDetail extends AutomationRunSummary {
  id: string;
}

function getIdToken(session: unknown) {
  if (!session || typeof session !== "object" || !("tokens" in session)) {
    throw new Error("No ID token is available. User is not logged in.");
  }

  const tokens = session.tokens;
  if (
    !tokens
    || typeof tokens !== "object"
    || !("id_token" in tokens)
    || typeof tokens.id_token !== "string"
  ) {
    throw new Error("No ID token is available. User is not logged in.");
  }

  return tokens.id_token;
}

export function usePdfReportDownload(options: UsePdfReportDownloadOptions) {
  const { profile } = useProfile();
  const { siteId } = useSite();
  const busy = shallowRef(false);
  const errorMessage = shallowRef("");
  let activeController: AbortController | null = null;

  function resolvePath(value: string, label: string) {
    return resolveFileApiPath(value, {
      userId: profile.value?.id,
      label,
      resourceDefinitionPath: toValue(options.resourceDefinitionPath),
    });
  }

  async function startAutomation(path: string) {
    const resolvedSiteId = siteId.value;
    if (!resolvedSiteId) {
      throw new Error("A site is required to generate the PDF report.");
    }

    const { session } = useUserSession();
    return useApiFetch<{ runId: string }>("/v1/automations/run", {
      method: "POST",
      body: {
        options: {
          path,
          inputs: {
            userId: profile.value?.id,
            siteId: resolvedSiteId,
            accessToken: getIdToken(session.value),
          },
          siteId: resolvedSiteId,
        },
      },
    });
  }

  async function downloadBlob(path: string, fileName: string) {
    const resolvedSiteId = siteId.value;
    if (!resolvedSiteId) {
      throw new Error("A site is required to download the PDF report.");
    }

    const blob = await useApiFetch<Blob>(
      `/v1/file/${encodeURIComponent(resolvedSiteId)}/download/${path}`,
      { responseType: "blob" },
    );
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    try {
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
    } finally {
      anchor.remove();
      window.URL.revokeObjectURL(url);
    }
  }

  async function execute() {
    if (busy.value) return false;
    if (import.meta.server) return false;

    busy.value = true;
    errorMessage.value = "";
    activeController = new AbortController();

    try {
      const rawReportPath = toValue(options.reportPath)?.trim() ?? "";
      if (!rawReportPath) {
        throw new Error("Configure a PDF report path before downloading.");
      }

      const reportPath = resolvePath(rawReportPath, "PDF report path");
      const rawAutomationPath = toValue(options.automationPath)?.trim() ?? "";

      if (rawAutomationPath) {
        const automationPath = resolvePath(rawAutomationPath, "Automation path");
        const result = await startAutomation(automationPath);
        if (!result?.runId) {
          throw new Error("The automation did not return a run identifier.");
        }

        const run = await waitForAutomationRun(result.runId, {
          signal: activeController.signal,
          load: (runId) =>
            useApiFetch<AutomationRunDetail>(
              `/v1/automations/runs/${encodeURIComponent(runId)}`,
            ),
        });
        assertAutomationRunCompleted(run);
      }

      await downloadBlob(
        reportPath,
        resolvePdfDownloadFileName(toValue(options.fileName), reportPath),
      );
      return true;
    } catch (error) {
      errorMessage.value = error instanceof Error
        ? error.message
        : "The PDF report could not be downloaded.";
      throw error;
    } finally {
      activeController = null;
      busy.value = false;
    }
  }

  onScopeDispose(() => activeController?.abort());

  return {
    busy: readonly(busy),
    errorMessage: readonly(errorMessage),
    execute,
  };
}
